/**
 * Auth Store (Zustand)
 *
 * In-memory authentication state management.
 * IMPORTANT: No persistence - state is cleared on page refresh.
 * Session persistence is handled by HttpOnly cookie (session_id).
 *
 * On app load, call initializeAuth() to restore user from session cookie.
 */

import { create } from 'zustand';
import { skipsPermissionChecks } from '@aiga/app-config/permissions';
import { authService, type User, type Permission, type Role } from '../services/auth.service.js';
import { HttpClientError } from '../api/http-client.js';

// Module-scoped in-flight tracker so concurrent callers (AuthProvider
// mount + every ProtectedRoute that lands before isInitialized flips)
// share a single /auth/me probe instead of stampeding the endpoint and
// blowing the global rate limit. Lives outside the Zustand store
// because the Zustand state setter is async and the duplicate
// `initializeAuth()` calls fire synchronously before any state update
// can land.
let inFlightInitialize: Promise<void> | null = null;

interface AuthState {
  // State
  user: User | null;
  permissions: Permission[];
  role: Role | null;
  /**
   * True while the signed-in user is operating on an admin-issued
   * temporary password. Populated from user_profiles.password_change_required
   * on login + initializeAuth. App.tsx uses this to route to /change-password
   * BEFORE rendering the dashboard so the user never sees a "flash" of
   * protected UI before being redirected.
   */
  passwordChangeRequired: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setPermissions: (permissions: Permission[]) => void;
  setRole: (role: Role | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  // Auth operations
  initializeAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
  updateUserProfile: (updates: Partial<User>) => void;

  // Helpers
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  permissions: [],
  role: null,
  passwordChangeRequired: false,
  isLoading: false,
  isInitialized: false,
  error: null,

  // Setters
  setUser: (user) => set({ user }),
  setPermissions: (permissions) => set({ permissions }),
  setRole: (role) => set({ role }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),

  // Initialize auth from session cookie
  initializeAuth: async () => {
    const state = get();
    if (state.isInitialized) return; // Already initialized

    // Dedupe concurrent callers. Without this guard, a fresh page load
    // fires `initializeAuth` from AuthProvider AND from every mounted
    // ProtectedRoute in the same tick — all of them pass the
    // `isInitialized` check (still false), all of them issue /auth/me +
    // /auth/permissions + /auth/profile in parallel. Across a few
    // navigations that's enough to trip the global rate limiter, get a
    // rate-limit response back on /auth/me, and (before this fix)
    // cascade into a forced logout.
    if (inFlightInitialize) return inFlightInitialize;

    set({ isLoading: true, error: null });

    inFlightInitialize = (async () => {
      try {
        // Try to get current user from session cookie
        const { user } = await authService.getCurrentUser();

        if (user) {
          set({ user });

          // Fetch permissions in parallel
          try {
            const { permissions } = await authService.getPermissions();
            set({ permissions });
          } catch (permError) {
            console.warn('[Auth] Failed to fetch permissions:', permError);
            // Non-critical, continue with empty permissions
          }

          // Fetch role + password-change-required flag from the profile
          // response. The profile payload is the raw user_profiles row, so
          // password_change_required lives there with snake_case naming.
          try {
            const { role, profile } = await authService.getProfile();
            set({
              role,
              passwordChangeRequired:
                (profile as any)?.password_change_required === true,
            });
          } catch (roleError) {
            console.warn('[Auth] Failed to fetch role/profile:', roleError);
            // Non-critical, continue without role
          }
        }

        set({ isInitialized: true, isLoading: false });
      } catch (error: any) {
        // Only treat 401 as "no session" — anything else (429, 5xx,
        // network blip) is transient and must not silently log the user
        // out. The previous behavior cleared `user: null` on every
        // error, which combined with a rate-limit response on /auth/me
        // produced a logout/login loop during local testing.
        const status =
          (error instanceof HttpClientError && error.statusCode) ||
          error?.statusCode ||
          error?.status ||
          0;
        const isUnauthenticated = status === 401;

        if (isUnauthenticated) {
          console.log('[Auth] No active session');
          set({
            user: null,
            permissions: [],
            role: null,
            passwordChangeRequired: false,
            isInitialized: true,
            isLoading: false,
            error: null,
          });
        } else {
          // Transient (429 / 5xx / network). Mark isInitialized so the
          // ProtectedRoute effect doesn't immediately retry in a tight
          // loop (which would compound any rate-limit backpressure), but
          // leave `user` untouched — if a prior call had populated it
          // the user stays logged in, and if not the router naturally
          // routes to /login.
          console.warn(
            `[Auth] Transient error during initializeAuth (status=${status}):`,
            error?.message || error
          );
          set({ isInitialized: true, isLoading: false });
        }
      } finally {
        inFlightInitialize = null;
      }
    })();

    return inFlightInitialize;
  },

  // Login
  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const { user } = await authService.login({ email, password });
      set({ user });

      // Fetch permissions and role
      try {
        const [permissionsRes, profileRes] = await Promise.all([
          authService.getPermissions(),
          authService.getProfile(),
        ]);

        set({
          permissions: permissionsRes.permissions,
          role: profileRes.role,
          passwordChangeRequired:
            (profileRes.profile as any)?.password_change_required === true,
        });
      } catch (err) {
        console.warn('[Auth] Failed to fetch permissions/role after login:', err);
      }

      set({ isLoading: false, isInitialized: true });
    } catch (error: any) {
      const errorMessage = error.message || 'Login failed';
      set({ error: errorMessage, isLoading: false });
      throw error; // Re-throw for component error handling
    }
  },

  // Logout
  logout: async () => {
    set({ isLoading: true, error: null });

    try {
      await authService.logout();
      set({
        user: null,
        permissions: [],
        role: null,
        passwordChangeRequired: false,
        isLoading: false,
        isInitialized: true, // Stay initialized — auth state is "logged out"
        error: null,
      });
    } catch (error: any) {
      // Even if logout fails, clear local state
      console.error('[Auth] Logout error:', error);
      set({
        user: null,
        permissions: [],
        role: null,
        passwordChangeRequired: false,
        isLoading: false,
        isInitialized: true, // Stay initialized — auth state is "logged out"
        error: error.message || 'Logout failed',
      });
    }
  },

  // Refresh permissions
  refreshPermissions: async () => {
    try {
      const { permissions } = await authService.getPermissions();
      set({ permissions });
    } catch (error: any) {
      console.error('[Auth] Failed to refresh permissions:', error);
      throw error;
    }
  },

  // Update user profile in store (after API update)
  updateUserProfile: (updates: Partial<User>) => {
    const { user } = get();
    if (user) {
      set({ user: { ...user, ...updates } });
    }
  },

  // Permission helpers
  // Super Admin and AIG staff skip permission checks (skipsPermissionChecks, the API middleware's rule);
  // Admin holds what its grants say.
  hasPermission: (permission: string) => {
    const { user, permissions, role } = get();
    if (!user) return false;
    if (skipsPermissionChecks({ role: role?.name, email: user.email, isAdmin: user.isAdmin })) return true;
    return (permissions ?? []).some((p) => p.name === permission);
  },

  hasAnyPermission: (permissionList: string[]) => {
    const { user, permissions, role } = get();
    if (!user) return false;
    if (skipsPermissionChecks({ role: role?.name, email: user.email, isAdmin: user.isAdmin })) return true;
    const perms = permissions ?? [];
    return permissionList.some((p) => perms.some((perm) => perm.name === p));
  },

  hasAllPermissions: (permissionList: string[]) => {
    const { user, permissions, role } = get();
    if (!user) return false;
    if (skipsPermissionChecks({ role: role?.name, email: user.email, isAdmin: user.isAdmin })) return true;
    const perms = permissions ?? [];
    return permissionList.every((p) => perms.some((perm) => perm.name === p));
  },

  isAdmin: () => {
    const { user } = get();
    return user?.isAdmin || false;
  },
}));
